import { useClerk, useUser } from '@clerk/clerk-react';
import { LogOut, MailCheck } from 'lucide-react';
import { useState } from 'react';
import { DoorlyLogo } from '../../components/DoorlyLogo';
import { Button } from '../../components/ui/button';
import { db } from '../../db/schema';
import { clearActingAs } from '../../lib/actingAs';
import { clearImpersonation } from '../../lib/impersonation';

/** Paramètres que Clerk pose sur le lien « Accept invitation ». */
export const INVITATION_TICKET_PARAM = '__clerk_ticket';

export function hasInvitationTicket(search = window.location.search): boolean {
  return new URLSearchParams(search).has(INVITATION_TICKET_PARAM);
}

/**
 * Un lien d'invitation ouvert avec une session déjà active était **ignoré
 * en silence** : l'app affichait le tableau de bord du compte connecté, le
 * ticket restait dans l'URL sans être consommé, et l'invitation restait
 * `pending` — sans un mot. Un admin qui crée un manager puis clique sur le
 * mail depuis le même navigateur tombe dessus à coup sûr (15/09/2026).
 *
 * L'instance est en session unique : accepter l'invitation exige de fermer
 * la session courante. On le dit, et on le fait sur le même lien — Clerk
 * revient ici déconnecté, le ticket encore dans l'URL, et l'écran de
 * connexion le consomme.
 */
export function InvitationTicketGate() {
  const { signOut } = useClerk();
  const { user } = useUser();
  const [leaving, setLeaving] = useState(false);
  const email = user?.primaryEmailAddress?.emailAddress ?? 'ce compte';

  const acceptWithOtherAccount = async () => {
    setLeaving(true);
    // Le prochain compte n'hérite ni d'un cache Dexie, ni d'un acteur PIN,
    // ni d'une imperso — même hygiène que `/logout`, mais en gardant l'URL.
    clearActingAs();
    clearImpersonation();
    await db.wipe();
    await signOut({ redirectUrl: window.location.href });
  };

  const stay = () => {
    const url = new URL(window.location.href);
    url.searchParams.delete(INVITATION_TICKET_PARAM);
    url.searchParams.delete('__clerk_status');
    window.location.replace(url.pathname + url.search);
  };

  return (
    <div className="min-h-dvh grid place-items-center bg-bg p-4">
      <div className="w-full max-w-md space-y-6">
        <header className="flex flex-col items-center gap-3">
          <DoorlyLogo variant="full" size={40} />
        </header>
        <div
          className="bg-surface p-6 space-y-4"
          style={{ borderRadius: 'var(--radius-card)', boxShadow: 'var(--shadow-card)' }}
        >
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-brand-soft grid place-items-center text-brand shrink-0">
              <MailCheck className="h-5 w-5" />
            </div>
            <div className="space-y-1">
              <h1 className="text-title font-extrabold text-ink">Une invitation vous attend</h1>
              <p className="text-body text-ink-mute">
                Vous êtes connecté en tant que <span className="font-semibold text-ink">{email}</span>.
                Pour accepter cette invitation avec un autre compte, il faut d'abord fermer cette
                session.
              </p>
            </div>
          </div>
          <div className="flex flex-col gap-2">
            <Button type="button" onClick={() => void acceptWithOtherAccount()} disabled={leaving}>
              <LogOut className="h-4 w-4" />
              {leaving ? 'Déconnexion…' : 'Se déconnecter et accepter l\'invitation'}
            </Button>
            <Button type="button" variant="ghost" onClick={stay} disabled={leaving}>
              Rester connecté en tant que {email}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
